import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  deleteWebsiteConversation,
  deleteWebsiteConversationMessage,
  getWebsiteChatSettings,
  getWebsiteConversationMessages,
  getWebsiteConversations,
  postWebsiteConversationMessage,
  updateWebsiteChatSettings,
  updateWebsiteConversation,
} from '@/lib/api';
import {
  CHAT_STATUSES,
  CONVERSATION_POLL_MS,
  MESSAGE_POLL_MS,
  badgeClass,
  chatStatusMeta,
  displayValue,
  formatDateTime,
  mergeMessages,
  relativeTime,
  sourcePageLabel,
} from '@/lib/websiteEngagement';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import useMediaQuery, { DESKTOP_QUERY } from '@/hooks/useMediaQuery';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Bot, Check, ChevronLeft, Copy, Loader2, Mail, MessageCircle, Plus, Send,
  Settings, Trash2, X,
} from 'lucide-react';

const inputClass =
  'border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const ALL = 'all';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function keywordsToText(keywords) {
  return safeList(keywords).join(', ');
}

/**
 * What an email to this visitor starts out saying.
 *
 * A chat reply and an email are different things: the chat is a live
 * exchange the visitor is watching, and an email is what you send when the
 * answer is longer than a bubble, needs an attachment, or the person has
 * closed the tab. So this opens a real email rather than another way of
 * typing into the thread.
 */
function emailDefaults(conversation) {
  return {
    subject: 'Following up on your message to Metropolitan Community College',
    body: 'Hello [First name],'
      + '\n\nThank you for getting in touch with us on our website.'
      + '\n\n\n\nKind regards,\nMetropolitan Community College',
  };
}

/**
 * The same, quoting one message.
 *
 * The quote is the whole point of the per-message action: without it the
 * visitor gets an email about "your message" and has to guess which of the
 * six things they asked is being answered.
 */
function emailAboutMessageDefaults(message) {
  const quoted = String(message?.text || '')
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
  return {
    subject: 'About your message to Metropolitan Community College',
    body: 'Hello [First name],'
      + '\n\nYou wrote to us on our website:'
      + `\n\n${quoted}`
      + '\n\n\n\nKind regards,\nMetropolitan Community College',
  };
}

export default function AdminWebsiteMessages() {
  // Two panes side by side is a desktop idea. On a phone there is room for
  // one, so the inbox becomes list-then-thread and this is what tells the
  // component which of the two it is showing.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [conversations, setConversations] = useState([]);
  const [unreadTotalCount, setUnreadTotalCount] = useState(0);
  const [activeId, setActiveId] = useState('');
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [onlineStaffCount, setOnlineStaffCount] = useState(0);
  const [needsStaffTotal, setNeedsStaffTotal] = useState(0);
  const [needsStaffOnly, setNeedsStaffOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ visitor_name: '', visitor_email: '' });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Deletion is permanent and full-admin only. The server says which it is;
  // the control is disabled with the reason rather than hidden, so a scoped
  // admin can see the action exists and who to ask.
  const [permissions, setPermissions] = useState({});
  const [permissionHints, setPermissionHints] = useState({});
  const [deleteConversationOpen, setDeleteConversationOpen] = useState(false);
  const [deleteMessageTarget, setDeleteMessageTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const bottomRef = useRef(null);
  const activeIdRef = useRef('');
  activeIdRef.current = activeId;

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const loadConversations = useCallback(async ({ quiet = false } = {}) => {
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== ALL) params.status = statusFilter;
      if (unreadOnly) params.unread = true;
      if (needsStaffOnly) params.needs_staff = true;
      const { data } = await getWebsiteConversations(params);
      const items = safeList(data?.items);
      setConversations(items);
      setUnreadTotalCount(Number(data?.unread_total) || 0);
      setNeedsStaffTotal(Number(data?.needs_staff_total) || 0);
      setOnlineStaffCount(Number(data?.online_staff_count) || 0);
      setQuickReplies(safeList(data?.quick_replies));
      setPermissions(data?.permissions || {});
      setPermissionHints(data?.permission_hints || {});
      // Opening the first conversation for you is a kindness on a wide screen,
      // where the list stays visible beside it. On a phone it would mean
      // landing in somebody's thread having never seen the inbox.
      setActiveId((current) => current || (isDesktop ? items[0]?.id || '' : ''));
    } catch (error) {
      console.error(error);
      if (!quiet) toast.error(error?.response?.data?.detail || 'Could not load website messages');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, unreadOnly, needsStaffOnly, isDesktop]);

  // Vercel functions cannot hold a socket open, so the inbox polls. Conversations
  // and the open thread refresh on their own timers.
  useEffect(() => {
    loadConversations();
    const timer = window.setInterval(() => loadConversations({ quiet: true }), CONVERSATION_POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadConversations]);

  const loadMessages = useCallback(async (conversationId, { quiet = false } = {}) => {
    if (!conversationId) return;
    try {
      const { data } = await getWebsiteConversationMessages(conversationId);
      // Merge rather than replace so a reply typed mid-poll is never dropped.
      setMessages((current) => (quiet
        ? mergeMessages(current, safeList(data?.messages))
        : safeList(data?.messages)));
    } catch (error) {
      console.error(error);
      if (!quiet) toast.error('Could not load this conversation');
    }
  }, []);

  useEffect(() => {
    if (!activeId) return undefined;
    setMessages([]);
    setEditingContact(false);
    loadMessages(activeId);
    const timer = window.setInterval(
      () => loadMessages(activeIdRef.current, { quiet: true }),
      MESSAGE_POLL_MS,
    );
    return () => window.clearInterval(timer);
  }, [activeId, loadMessages]);

  // The heartbeat lives in the app shell (useStaffPresence), so staff count as
  // available anywhere in the portal. This screen only reports what the server
  // says — running a second timer here would double the beats for one person.

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeId) || null,
    [activeId, conversations],
  );

  const canDelete = permissions['messages.delete'] === true;
  const deleteHint = permissionHints['messages.delete']
    || 'Only a full administrator can delete a conversation or a message.';

  useEffect(() => {
    setContactDraft({
      visitor_name: activeConversation?.visitor_name || '',
      visitor_email: activeConversation?.visitor_email || '',
    });
  }, [activeConversation]);

  const patchConversation = async (payload, successMessage) => {
    if (!activeId) return;
    try {
      const { data } = await updateWebsiteConversation(activeId, payload);
      setConversations((current) => current.map(
        (conversation) => (conversation.id === activeId ? { ...conversation, ...data } : conversation),
      ));
      if (successMessage) toast.success(successMessage);
      loadConversations({ quiet: true });
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update this conversation');
    }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email || '');
      toast.success('Email address copied');
    } catch (error) {
      console.error(error);
      toast.error('Could not copy — the address is shown above.');
    }
  };

  const send = async (text) => {
    const body = String(text ?? draft).trim();
    if (!body || !activeId || sending) return;
    try {
      setSending(true);
      const { data } = await postWebsiteConversationMessage(activeId, { text: body });
      setMessages((current) => mergeMessages(current, [data]));
      setDraft('');
      // Say what actually happened to the reply rather than assuming it landed.
      if (data?.delivery === 'emailed') toast.success('Visitor had left — reply sent by email');
      else if (data?.email_follow_up_required) {
        toast.error('Visitor had left and the email could not be sent — follow up by email');
      }
      loadConversations({ quiet: true });
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  const removeConversation = async () => {
    if (!activeId || deleting) return;
    try {
      setDeleting(true);
      const { data } = await deleteWebsiteConversation(activeId);
      const removed = Number(data?.messages_deleted) || 0;
      toast.success(
        `Conversation deleted with ${removed} message${removed === 1 ? '' : 's'}`,
      );
      setDeleteConversationOpen(false);
      // Back to the list: the thread that was on screen no longer exists,
      // so leaving it open would be showing a record that is gone.
      setActiveId('');
      setMessages([]);
      await loadConversations({ quiet: true });
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete this conversation');
    } finally {
      setDeleting(false);
    }
  };

  const removeMessage = async () => {
    const target = deleteMessageTarget;
    if (!target || deleting) return;
    try {
      setDeleting(true);
      await deleteWebsiteConversationMessage(target.conversation_id || activeId, target.id);
      toast.success('Message deleted');
      setDeleteMessageTarget(null);
      // Replace rather than merge: a merge would put the deleted message
      // straight back on screen from what is already in state.
      setMessages((current) => current.filter((message) => message.id !== target.id));
      await loadMessages(activeIdRef.current);
      await loadConversations({ quiet: true });
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete this message');
    } finally {
      setDeleting(false);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    try {
      const { data } = await getWebsiteChatSettings();
      setSettings({
        fallback_message: data?.fallback_message || '',
        away_message: data?.away_message || '',
        faq_rules: safeList(data?.faq_rules).map((rule) => ({
          ...rule, keywordsText: keywordsToText(rule.keywords),
        })),
        quick_replies: safeList(data?.quick_replies),
      });
    } catch (error) {
      console.error(error);
      toast.error('Could not load the automatic reply settings');
    }
  };

  const saveSettings = async () => {
    if (!settings || savingSettings) return;
    try {
      setSavingSettings(true);
      const { data } = await updateWebsiteChatSettings({
        fallback_message: settings.fallback_message,
        away_message: settings.away_message,
        faq_rules: settings.faq_rules.map((rule) => ({
          id: rule.id,
          label: rule.label,
          priority: Number(rule.priority) || 100,
          enabled: !!rule.enabled,
          keywords: rule.keywordsText,
          answer: rule.answer,
        })),
        quick_replies: settings.quick_replies,
      });
      setSettings({
        fallback_message: data?.fallback_message || '',
        away_message: data?.away_message || '',
        faq_rules: safeList(data?.faq_rules).map((rule) => ({
          ...rule, keywordsText: keywordsToText(rule.keywords),
        })),
        quick_replies: safeList(data?.quick_replies),
      });
      setQuickReplies(safeList(data?.quick_replies));
      toast.success('Automatic replies saved');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save these settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const setRule = (index, patch) => setSettings((current) => ({
    ...current,
    faq_rules: current.faq_rules.map((rule, position) => (
      position === index ? { ...rule, ...patch } : rule
    )),
  }));

  const addRule = () => setSettings((current) => ({
    ...current,
    faq_rules: [...current.faq_rules, {
      id: `rule_${current.faq_rules.length + 1}`,
      label: 'New answer',
      priority: (current.faq_rules.length + 1) * 10,
      enabled: true,
      keywordsText: '',
      answer: '',
    }],
  }));

  const removeRule = (index) => setSettings((current) => ({
    ...current,
    faq_rules: current.faq_rules.filter((_, position) => position !== index),
  }));

  const setQuickReply = (index, patch) => setSettings((current) => ({
    ...current,
    quick_replies: current.quick_replies.map((reply, position) => (
      position === index ? { ...reply, ...patch } : reply
    )),
  }));

  const addQuickReply = () => setSettings((current) => ({
    ...current,
    quick_replies: [...current.quick_replies, {
      id: `reply_${current.quick_replies.length + 1}`, label: 'New reply', text: '',
    }],
  }));

  const removeQuickReply = (index) => setSettings((current) => ({
    ...current,
    quick_replies: current.quick_replies.filter((_, position) => position !== index),
  }));

  return (
    <WebsiteEngagementShell
      title="Online Chat Messages"
      description={
        'Conversations with visitors on the public website. Separate from Team Chat — '
        + 'everything you write here goes to the person on the other end.'
      }
      actions={(
        <>
          <span
            data-testid="presence-indicator"
            title="What the public website shows visitors right now"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
              onlineStaffCount > 0
                ? 'border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.10)] text-[rgb(21,128,61)]'
                : 'border-[rgba(6,43,26,0.12)] bg-[rgba(6,43,26,0.04)] text-[#062B1A]/60',
            )}
          >
            <span className={cn('h-2 w-2 rounded-full', onlineStaffCount > 0 ? 'bg-[rgb(21,128,61)]' : 'bg-[#062B1A]/35')} />
            {onlineStaffCount > 0
              ? `Online — ${onlineStaffCount} staff`
              : 'Offline'}
          </span>
          {needsStaffTotal > 0 && (
            <Badge variant="outline" className={badgeClass('danger')}>
              {needsStaffTotal} waiting for a person
            </Badge>
          )}
          {unreadTotalCount > 0 && (
            <Badge variant="outline" className={badgeClass('warning')}>
              {unreadTotalCount} unread
            </Badge>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => (settingsOpen ? setSettingsOpen(false) : openSettings())}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            <Settings className="mr-1.5 h-4 w-4" />
            {settingsOpen ? 'Back to inbox' : 'Automatic replies'}
          </Button>
        </>
      )}
    >
      {settingsOpen ? (
        <SettingsPanel
          settings={settings}
          saving={savingSettings}
          onChange={setSettings}
          onSave={saveSettings}
          setRule={setRule}
          addRule={addRule}
          removeRule={removeRule}
          setQuickReply={setQuickReply}
          addQuickReply={addQuickReply}
          removeQuickReply={removeQuickReply}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          {/* Left pane — conversations. Stands aside on a phone once a
              thread is open; both panes are always here on a wide screen. */}
          <Card className={cn(
            'border border-[rgba(6,43,26,0.08)] shadow-sm',
            activeConversation && 'hidden lg:block',
          )}>
            <CardContent className="space-y-3 p-3">
              <Input
                type="search"
                aria-label="Search conversations"
                placeholder="Search name, email, or page..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className={inputClass}
              />
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger aria-label="Status filter" className={cn('h-9 flex-1', inputClass)}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    {CHAT_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{chatStatusMeta(status).label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant={unreadOnly ? 'default' : 'outline'}
                  size="sm"
                  aria-pressed={unreadOnly}
                  onClick={() => setUnreadOnly((current) => !current)}
                  className={unreadOnly
                    ? 'bg-[#062B1A] text-white hover:bg-[#041B10]'
                    : 'border-[rgba(6,43,26,0.16)] text-[#062B1A]'}
                >
                  Unread
                </Button>
                <Button
                  type="button"
                  variant={needsStaffOnly ? 'default' : 'outline'}
                  size="sm"
                  aria-pressed={needsStaffOnly}
                  title="Visitors who asked to speak with a person"
                  onClick={() => setNeedsStaffOnly((current) => !current)}
                  className={needsStaffOnly
                    ? 'bg-[#062B1A] text-white hover:bg-[#041B10]'
                    : 'border-[rgba(6,43,26,0.16)] text-[#062B1A]'}
                >
                  Needs a person
                </Button>
              </div>

              <div className="max-h-[60vh] space-y-1 overflow-y-auto lg:max-h-[30rem]">
                {loading ? (
                  <p className="flex items-center gap-2 px-2 py-3 text-sm text-[#062B1A]/50">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </p>
                ) : conversations.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm text-[#062B1A]/45">
                    No conversations yet. Messages from the website appear here.
                  </p>
                ) : (
                  conversations.map((conversation) => {
                    const meta = chatStatusMeta(conversation.status);
                    const unread = Number(conversation.unread_count) || 0;
                    return (
                      <button
                        key={conversation.id}
                        type="button"
                        data-testid="conversation-row"
                        onClick={() => setActiveId(conversation.id)}
                        className={cn(
                          'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                          conversation.id === activeId
                            ? 'border-[#062B1A] bg-[#062B1A] text-white'
                            : 'border-transparent text-[#062B1A] hover:bg-[#F1F3F5]',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('truncate text-sm', unread > 0 ? 'font-bold' : 'font-medium')}>
                            {displayValue(conversation.visitor_name)}
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            {conversation.needs_staff && (
                              <span
                                data-testid="needs-staff-badge"
                                title="Asked to speak with a person"
                                className="rounded-full bg-[rgb(153,27,27)] px-1.5 py-0.5 text-[10px] font-semibold text-white"
                              >
                                Person
                              </span>
                            )}
                            {conversation.email_follow_up_required && (
                              <Mail
                                data-testid="email-follow-up-icon"
                                aria-label="Email follow-up required"
                                className="size-3.5 text-[rgb(153,27,27)]"
                              />
                            )}
                            {unread > 0 && (
                              <span
                                data-testid="unread-badge"
                                className="rounded-full bg-[#D4AF37] px-1.5 py-0.5 text-[10px] font-semibold text-[#062B1A]"
                              >
                                {unread}
                              </span>
                            )}
                          </span>
                        </div>
                        <p className={cn(
                          'truncate text-xs',
                          conversation.id === activeId ? 'text-white/70' : 'text-[#062B1A]/55',
                        )}
                        >
                          {conversation.last_message_preview || conversation.visitor_email}
                        </p>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className={cn(
                            'text-[11px]',
                            conversation.id === activeId ? 'text-white/60' : 'text-[#062B1A]/45',
                          )}
                          >
                            {relativeTime(conversation.last_message_at)}
                          </span>
                          {conversation.id !== activeId && (
                            <Badge variant="outline" className={cn('px-1.5 py-0 text-[10px]', badgeClass(meta.tone))}>
                              {meta.label}
                            </Badge>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right pane — the selected thread. Hidden on a phone until there
              is one to show, so the empty state does not push the list off
              the screen it is the only content of. */}
          <Card className={cn(
            'flex min-h-[34rem] flex-col border border-[rgba(6,43,26,0.08)] shadow-sm',
            !activeConversation && 'hidden lg:flex',
          )}>
            {!activeConversation ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
                <MessageCircle className="h-8 w-8 text-[#062B1A]/20" />
                <p className="text-sm text-[#062B1A]/50">Pick a conversation to read and reply.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 border-b border-[rgba(6,43,26,0.08)] p-4">
                  {/* The only way back to the inbox on a phone, where the list
                      is not on screen beside this. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="back-to-conversations"
                    onClick={() => setActiveId('')}
                    className="-ml-2 h-8 px-2 text-xs text-[#062B1A]/70 lg:hidden"
                  >
                    <ChevronLeft className="mr-1 size-4" />
                    All conversations
                  </Button>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      {editingContact ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            aria-label="Visitor name"
                            value={contactDraft.visitor_name}
                            onChange={(event) => setContactDraft(
                              (current) => ({ ...current, visitor_name: event.target.value }),
                            )}
                            className={cn('h-8 w-44', inputClass)}
                          />
                          <Input
                            aria-label="Visitor email"
                            value={contactDraft.visitor_email}
                            onChange={(event) => setContactDraft(
                              (current) => ({ ...current, visitor_email: event.target.value }),
                            )}
                            className={cn('h-8 w-60', inputClass)}
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                            onClick={async () => {
                              await patchConversation(contactDraft, 'Contact details saved');
                              setEditingContact(false);
                            }}
                          >
                            <Check className="mr-1 h-3.5 w-3.5" /> Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                            onClick={() => setEditingContact(false)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p
                            className="truncate text-base font-semibold text-[#062B1A]"
                            style={{ fontFamily: 'var(--font-display)' }}
                          >
                            {displayValue(activeConversation.visitor_name)}
                          </p>
                          {/* The email stays in plain sight: it is how staff
                              reach this person once they close the tab. */}
                          <p className="truncate text-xs font-medium text-[#062B1A]/75">
                            <a
                              href={`mailto:${activeConversation.visitor_email}`}
                              className="underline decoration-[#D4AF37] underline-offset-2 hover:text-[#062B1A]"
                            >
                              {displayValue(activeConversation.visitor_email)}
                            </a>
                          </p>
                          <p className="truncate text-[11px] text-[#062B1A]/50">
                            {sourcePageLabel(activeConversation.source_page)}
                            {' · started '}
                            {formatDateTime(activeConversation.created_at)}
                          </p>
                          <p className="text-[11px] text-[#062B1A]/45">
                            {activeConversation.visitor_active
                              ? 'Visitor is on the page — a reply appears live'
                              : 'Visitor has left the page — a reply is emailed instead'}
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {!editingContact && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingContact(true)}
                          className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                        >
                          Edit details
                        </Button>
                      )}
                      <Select
                        value={activeConversation.status || 'new'}
                        onValueChange={(value) => patchConversation({ status: value }, 'Status updated')}
                      >
                        <SelectTrigger aria-label="Conversation status" className={cn('h-8 w-[11rem]', inputClass)}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHAT_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>{chatStatusMeta(status).label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!Number(activeConversation.unread_count)}
                        onClick={() => patchConversation({ mark_read: true }, 'Marked as read')}
                        className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                      >
                        Mark read
                      </Button>
                      {/* An email to the visitor, through the portal's own
                          composer — the same one Admissions and Accounting
                          use, so there is one sending path and one record
                          of what left the building. */}
                      <PortalEmailComposer
                        audience="chat_conversations"
                        recipients={[{
                          id: activeConversation.id,
                          label: activeConversation.visitor_name,
                          email: activeConversation.visitor_email,
                        }]}
                        buttonLabel="Email visitor"
                        defaults={emailDefaults(activeConversation)}
                        buttonClassName="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                        onSent={() => loadConversations({ quiet: true })}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid="delete-conversation"
                        disabled={!canDelete || deleting}
                        title={canDelete ? undefined : deleteHint}
                        onClick={() => setDeleteConversationOpen(true)}
                        className="border-red-200 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete conversation
                      </Button>
                    </div>
                  </div>

                  {activeConversation.needs_staff && (
                    <div
                      data-testid="needs-staff-banner"
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.9)] px-3 py-2"
                    >
                      <p className="text-xs text-[rgb(153,27,27)]">
                        This visitor asked to speak with a person. Automatic answers are switched
                        off for this conversation.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => patchConversation({ needs_staff: false }, 'Marked as handled')}
                        className="border-[rgba(153,27,27,0.3)] text-[rgb(153,27,27)]"
                      >
                        Mark handled
                      </Button>
                    </div>
                  )}

                  {/* Never implies the visitor got the message: this is the
                      state where they demonstrably did not. */}
                  {activeConversation.email_follow_up_required && (
                    <div
                      data-testid="email-follow-up-banner"
                      className="space-y-2 rounded-lg border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.08)] px-3 py-2"
                    >
                      <p className="text-xs font-semibold text-[#8A6508]">Email follow-up required</p>
                      <p className="text-xs text-[#062B1A]/70">
                        {activeConversation.email_follow_up_reason
                          || 'The last reply did not reach this visitor.'}
                        {' Send it by email yourself, then mark this cleared.'}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href={`mailto:${activeConversation.visitor_email}?subject=${encodeURIComponent('Reply from Metropolitan Community College')}`}
                          className="inline-flex h-8 items-center rounded-md border border-[rgba(6,43,26,0.16)] px-3 text-xs font-medium text-[#062B1A] hover:bg-white"
                        >
                          <Mail className="mr-1.5 size-3.5" />
                          Email {activeConversation.visitor_email}
                        </a>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => copyEmail(activeConversation.visitor_email)}
                          className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
                        >
                          <Copy className="mr-1.5 size-3.5" />
                          Copy address
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => patchConversation(
                            { email_follow_up_required: false }, 'Follow-up cleared',
                          )}
                          className="h-8 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
                        >
                          Mark sent
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                  {messages.length === 0 ? (
                    <p className="text-sm text-[#062B1A]/45">No messages in this conversation yet.</p>
                  ) : (
                    messages.map((message) => {
                      const fromStaff = message.sender_type === 'staff';
                      const automatic = message.sender_type === 'automatic';
                      return (
                        <div
                          key={message.id}
                          data-testid="chat-message"
                          data-sender={message.sender_type}
                          className={cn(
                            'rounded-lg border p-3',
                            automatic
                              ? 'border-dashed border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.06)]'
                              : fromStaff
                                ? 'border-[rgba(6,43,26,0.10)] bg-[#F7F8FA]'
                                : 'border-[rgba(6,43,26,0.10)] bg-white',
                          )}
                        >
                          <div className="mb-1 flex items-center gap-2">
                            {automatic && <Bot className="h-3.5 w-3.5 text-[#8A6508]" />}
                            <span className="text-xs font-semibold text-[#062B1A]">
                              {automatic
                                ? 'Automatic reply'
                                : fromStaff
                                  ? message.sender_name || 'You'
                                  : message.sender_name || 'Visitor'}
                            </span>
                            <span className="text-[11px] text-[#062B1A]/45">
                              {formatDateTime(message.created_at)}
                            </span>
                            {/* Email about this one message. Rendered per
                                message rather than driven from a single
                                composer because the quoted text is what
                                makes the action worth having, and it
                                differs for every row. */}
                            <span
                              data-testid="email-about-message"
                              className={cn('ml-auto', canDelete && 'ml-auto')}
                            >
                              <PortalEmailComposer
                                audience="chat_conversations"
                                recipients={activeConversation ? [{
                                  id: activeConversation.id,
                                  label: activeConversation.visitor_name,
                                  email: activeConversation.visitor_email,
                                }] : []}
                                iconOnly
                                buttonLabel={`Email about this message from ${message.sender_name || "the thread"}`}
                                buttonVariant="ghost"
                                defaults={emailAboutMessageDefaults(message)}
                                buttonClassName="h-6 w-6 p-0 text-[#062B1A]/35 hover:text-[#062B1A]"
                                onSent={() => loadConversations({ quiet: true })}
                              />
                            </span>
                            {canDelete && (
                              <button
                                type="button"
                                data-testid="delete-message"
                                aria-label={`Delete this message from ${message.sender_name || 'the thread'}`}
                                title="Delete this message"
                                disabled={deleting}
                                onClick={() => setDeleteMessageTarget(message)}
                                className="rounded p-1 text-[#062B1A]/35 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          {/* Visitor text is rendered as text, never as HTML. */}
                          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#062B1A]/85">
                            {message.text}
                          </p>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>

                <div className="space-y-2 border-t border-[rgba(6,43,26,0.08)] p-3">
                  {quickReplies.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {quickReplies.map((reply) => (
                        <Button
                          key={reply.id}
                          type="button"
                          size="sm"
                          variant="outline"
                          data-testid="quick-reply"
                          disabled={sending}
                          onClick={() => setDraft(reply.text)}
                          className="h-7 border-[rgba(6,43,26,0.16)] text-xs text-[#062B1A]"
                        >
                          {reply.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Textarea
                    aria-label="Reply to visitor"
                    rows={3}
                    value={draft}
                    disabled={sending}
                    placeholder="Write a reply…"
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) send();
                    }}
                    className={inputClass}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-[#062B1A]/40">Ctrl+Enter to send</p>
                    <Button
                      type="button"
                      onClick={() => send()}
                      disabled={sending || !draft.trim()}
                      className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    >
                      {sending
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Send className="mr-2 h-4 w-4" />}
                      Send
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      <DeleteConversationDialog
        open={deleteConversationOpen}
        conversation={activeConversation}
        busy={deleting}
        onCancel={() => setDeleteConversationOpen(false)}
        onConfirm={removeConversation}
      />
      <DeleteMessageDialog
        open={!!deleteMessageTarget}
        message={deleteMessageTarget}
        busy={deleting}
        onCancel={() => setDeleteMessageTarget(null)}
        onConfirm={removeMessage}
      />
    </WebsiteEngagementShell>
  );
}

/**
 * Deleting a whole conversation.
 *
 * The dialog names how many messages go with it, because that is the part
 * somebody is most likely to be wrong about: the thread on screen may be
 * longer than the few messages visible in the scroll area.
 */
function DeleteConversationDialog({ open, conversation, busy, onCancel, onConfirm }) {
  const count = Number(conversation?.message_count) || 0;
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete this conversation?</DialogTitle>
          <DialogDescription>
            <span className="block text-[#062B1A]">
              {displayValue(conversation?.visitor_name)}
              {conversation?.visitor_email ? ` · ${conversation.visitor_email}` : ''}
            </span>
            <span className="mt-1 block">
              {`The whole thread goes — all ${count} message${count === 1 ? '' : 's'}, `}
              the visitor&apos;s and ours. This cannot be undone.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            Keep it
          </Button>
          <Button
            type="button"
            data-testid="confirm-delete-conversation"
            disabled={busy}
            onClick={onConfirm}
            className="bg-red-700 text-white hover:bg-red-800"
          >
            {busy
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Trash2 className="mr-2 h-4 w-4" />}
            Delete conversation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Deleting one message.
 *
 * It quotes the message back rather than asking about "this message": in a
 * thread of near-identical automatic replies, the quote is the only thing
 * that tells you which one you are about to remove.
 */
function DeleteMessageDialog({ open, message, busy, onCancel, onConfirm }) {
  const sender = message?.sender_type === 'staff'
    ? 'a staff reply'
    : message?.sender_type === 'automatic' ? 'an automatic reply' : 'the visitor';
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete this message?</DialogTitle>
          <DialogDescription>
            {`One message from ${sender} is removed. The rest of the conversation stays, `}
            and its preview and unread count are recalculated. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <p
          data-testid="delete-message-quote"
          className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A]/75"
        >
          {message?.text}
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            Keep it
          </Button>
          <Button
            type="button"
            data-testid="confirm-delete-message"
            disabled={busy}
            onClick={onConfirm}
            className="bg-red-700 text-white hover:bg-red-800"
          >
            {busy
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Trash2 className="mr-2 h-4 w-4" />}
            Delete message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel({
  settings, saving, onChange, onSave,
  setRule, addRule, removeRule,
  setQuickReply, addQuickReply, removeQuickReply,
}) {
  if (!settings) {
    return (
      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="flex items-center gap-2 p-8 text-sm text-[#062B1A]/50">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="space-y-4 p-5">
          <div>
            <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Fixed responses
            </h3>
            <p className="text-sm text-[#062B1A]/60">
              These are exact texts, matched on keywords in a set order. Nothing here is generated —
              a visitor only ever sees wording you wrote.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fallback_message">When no rule matches</Label>
              <Textarea
                id="fallback_message"
                rows={3}
                value={settings.fallback_message}
                onChange={(event) => onChange({ ...settings, fallback_message: event.target.value })}
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="away_message">When nobody is online</Label>
              <Textarea
                id="away_message"
                rows={3}
                value={settings.away_message}
                onChange={(event) => onChange({ ...settings, away_message: event.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Automatic answers
              </h3>
              <p className="text-sm text-[#062B1A]/60">
                Lowest priority number is checked first. Keywords are comma separated.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addRule}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add answer
            </Button>
          </div>

          <div className="space-y-3">
            {settings.faq_rules.length === 0 && (
              <p className="text-sm text-[#062B1A]/45">
                No automatic answers. Every message will get the fallback text above.
              </p>
            )}
            {settings.faq_rules.map((rule, index) => (
              <div
                key={rule.id || index}
                data-testid="faq-rule"
                className="space-y-3 rounded-lg border border-[rgba(6,43,26,0.10)] p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <Input
                    aria-label={`Answer name ${index + 1}`}
                    value={rule.label || ''}
                    onChange={(event) => setRule(index, { label: event.target.value })}
                    className={cn('h-8 w-56', inputClass)}
                  />
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={`priority-${index}`} className="text-xs text-[#062B1A]/60">Priority</Label>
                    <Input
                      id={`priority-${index}`}
                      type="number"
                      min={1}
                      value={rule.priority ?? 100}
                      onChange={(event) => setRule(index, { priority: event.target.value })}
                      className={cn('h-8 w-20', inputClass)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!rule.enabled}
                      onCheckedChange={(value) => setRule(index, { enabled: !!value })}
                      aria-label={`Enable ${rule.label || `answer ${index + 1}`}`}
                    />
                    <span className="text-xs text-[#062B1A]/60">{rule.enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove ${rule.label || `answer ${index + 1}`}`}
                    onClick={() => removeRule(index)}
                    className="ml-auto h-8 w-8 text-[rgb(153,27,27)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`keywords-${index}`} className="text-xs text-[#062B1A]/60">Keywords</Label>
                  <Input
                    id={`keywords-${index}`}
                    value={rule.keywordsText || ''}
                    onChange={(event) => setRule(index, { keywordsText: event.target.value })}
                    placeholder="what time, schedule, start time"
                    className={cn('h-8', inputClass)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`answer-${index}`} className="text-xs text-[#062B1A]/60">Answer</Label>
                  <Textarea
                    id={`answer-${index}`}
                    rows={3}
                    value={rule.answer || ''}
                    onChange={(event) => setRule(index, { answer: event.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Staff quick replies
              </h3>
              <p className="text-sm text-[#062B1A]/60">
                One-click buttons in the inbox. Clicking one drops the text into the reply box so you
                can adjust it before sending.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addQuickReply}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add quick reply
            </Button>
          </div>

          <div className="space-y-3">
            {settings.quick_replies.map((reply, index) => (
              <div
                key={reply.id || index}
                data-testid="quick-reply-setting"
                className="flex flex-wrap items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] p-3"
              >
                <Input
                  aria-label={`Quick reply name ${index + 1}`}
                  value={reply.label || ''}
                  onChange={(event) => setQuickReply(index, { label: event.target.value })}
                  className={cn('h-8 w-48', inputClass)}
                />
                <Textarea
                  aria-label={`Quick reply text ${index + 1}`}
                  rows={2}
                  value={reply.text || ''}
                  onChange={(event) => setQuickReply(index, { text: event.target.value })}
                  className={cn('min-w-[16rem] flex-1', inputClass)}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove quick reply ${index + 1}`}
                  onClick={() => removeQuickReply(index)}
                  className="h-8 w-8 text-[rgb(153,27,27)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
}
